import { BLEND_MODES, Container, Graphics, SCALE_MODES, Sprite, Texture } from "pixi.js"
import { Coordinate, LavaEmbersConfig, TileSize } from "game-common/models"
import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { ScalingParams } from "../../../../client_util"

// One spark or one bubble. Plain numbers, mutated in place; nothing in the loop allocates.
interface Mote {
    sprite: Sprite
    // Where on the GROUND it came off, and how high it has risen since -- drawn at (x, groundY - lift)
    // the way FootDust does it, so a rising spark does not slide out of the cell that made it.
    x: number
    groundY: number
    lift: number
    vx: number
    liftV: number
    ageMs: number
    lifeMs: number
    scale: number
    peakAlpha: number
}

/**
 * Lava, moving.
 *
 * WHY THIS IS NOT ANIMATED TILES, which is the thing anybody would try first and the one thing that
 * must not be done here. Lava is painted on `map4`, a TILEMAP layer (docs/ember-cave-lava.md §4), and
 * any dirty cell on a non-sprited layer escalates `repaintDirtyCells` to a full `renderAllLayers()`
 * over every cell of every layer (`map_rendering_manager.ts:755-766`, `:229`). `CompositeTilemap` is
 * append-and-clear only, so there is no such thing as repainting one cell of it -- and the renderer
 * has no animated-tile support at all. Cycling a lava tile through four frames at 8fps is therefore
 * eight full-map rerenders a second. The art stays still and everything that moves is a sprite.
 *
 * WHAT IT COSTS IS THE NUMBER OF SPARKS, NOT THE SIZE OF THE POOL. Emission is a tempo against the
 * visible cell count with a hard cap on both the rate and the live pool, so a 25-cell flood and a
 * 1-cell dribble cost the same at the cap. That matters because the budget-hardware target is about
 * frame hitches rather than throughput, and because the pool's size is the one thing about this
 * feature a player controls.
 *
 * TWO THINGS COME OFF IT, and they are two things because they sort differently:
 *
 *   SPARKS rise off the surface, drift, cool from white-hot through orange to a dull red, and wink
 *   out. They are drawn ABOVE the bodies in the room (see Z_INDEX_AIR) -- the same argument the
 *   fireflies make: a spark is light in the air, so a 3px additive dot crossing a player's shoulder
 *   reads as a spark in front of them rather than as a sort error. The honest cost of that choice is
 *   that sparks off a pool to the NORTH of you also draw over you; per-particle sorting against
 *   bodies is possible (FootDust parents each puff into the surface for exactly that) but it trades
 *   one batched draw call for N sort entries and N chances to break the batch, which is the wrong
 *   trade for ambience.
 *
 *   BUBBLES swell on the surface and burst. They are drawn BELOW every body (Z_INDEX_GROUND), because
 *   a bubble is the lava itself turning over -- it is on the floor, and a bubble painted up a player's
 *   shins is the artifact that choice avoids. They are also what makes the pool read as liquid at all,
 *   which is the job the animated tile would have done.
 *
 * HOW IT FINDS THE LAVA, and this is the part that keeps it free. Not a registry, not a cache
 * invalidated off the delta path, and never a scan of the room: every REFRESH_MS it walks the on-screen
 * box only -- about 200 cells -- asking the provider the question it already answers,
 * `isLavaAt` (`tile_map_meta_provider.ts:723`). The client keeps its own type sets in sync on every
 * delta (`map_rendering_manager.ts` -> `addTileAt`), so a runtime flood turns up by itself within a
 * quarter second, which is well inside the pace the flood is revealed at anyway. ~200 Set lookups four
 * times a second is nothing, it needs no coupling to the renderer, and it follows the camera for free.
 * A room with no lava pays one scan per refresh and draws nothing; a room that never asks for the
 * effect pays nothing at all, because `resolve` returns null before the scan.
 *
 * NO LIGHT IS REGISTERED HERE, deliberately. The sight-polygon mask costs ~2.6ms per frame in a cave
 * and is cached on a hash that includes `lights.lastUpdatedTs` (`shadow_rendering.ts:587`), so a
 * flickering lava light would re-bake it every frame even with the player standing still. The glow is
 * the art's job (and §9 of the design doc wants a tile-sourced light channel, which is its own piece
 * of work). Nothing here touches the mask.
 *
 * NO FILTERS, for the same reason the rest of the effects bake their art: a glow filter wants a render
 * target for what a stack of translucent circles does once, at startup, into one texture.
 *
 * DRIVEN OFF THE RENDER LOOP, never a timer. In a backgrounded tab rAF throttles to zero, and a timer
 * would keep emitting sparks into a world nobody is drawing (FootDust's header has the long version).
 *
 * LIVE TUNING, from the browser console -- no reload:
 *   window.lavaEmbers = true | false    force it on in ANY room, or off in the mine
 *   window.lavaEmberRate = 1.2          sparks per second per visible lava cell
 *   window.lavaBubbleRate = 1.0         bubbles per second across the whole visible pool
 */
export class LavaEmbers {
    // Sparks: above every body in the room, below the fog (890000) and the sky. See the header.
    readonly airContainer: Container
    // Bubbles: above all tile art (tilemap layers bake at zIndex 0) and below every entity, whose own
    // z-index starts at 100 + location.y.
    readonly groundContainer: Container

    static readonly Z_INDEX_AIR = 880000
    static readonly Z_INDEX_GROUND = 90

    // Hard caps. These are what make the cost independent of how much lava is on screen, which is the
    // whole performance argument -- a player who floods half the mine gets the same frame as one who
    // broke a single pocket.
    static readonly MAX_EMBERS = 90
    static readonly MAX_BUBBLES = 14
    static readonly MAX_EMBER_RATE = 36
    static readonly MAX_BUBBLE_RATE = 6

    private static readonly DEFAULT_EMBER_RATE = 1.1
    private static readonly DEFAULT_BUBBLE_RATE = 0.85
    private static readonly DEFAULT_ALPHA = 0.9
    private static readonly DEFAULT_SCALE_MIN = 0.4
    private static readonly DEFAULT_SCALE_MAX = 0.85
    // The cooling ramp, hottest first: a spark leaves the surface near white, goes orange, and dies a
    // dull red. The colour is most of what says "this is molten" -- a spark that stays one shade reads
    // as a firefly in a bad mood.
    private static readonly DEFAULT_EMBER_COLORS = [0xfff1c4, 0xffa33c, 0xd8431a]
    // Bubbles are the body of the lava rather than the gas off it, so they sit at the orange-to-red end
    // and never go bright: a white bubble reads as a splash of water catching the light.
    private static readonly DEFAULT_BUBBLE_COLORS = [0xff8a2a, 0xe8541c, 0xff6a24]

    // How high a spark gets, in world px/sec at birth, and the e-folds per second that bleeds it away.
    // Sparks rise and STOP rather than rising forever: hot gas loses on buoyancy, and a dot that
    // travels a tile upward reads as a firework.
    private static readonly RISE = 26
    private static readonly RISE_MIN = 0.6
    private static readonly RISE_MAX = 1.45
    private static readonly RISE_DRAG = 1.8
    // Sideways drift, which is what stops a pool looking like a row of vertical tracers.
    private static readonly DRIFT = 7
    private static readonly EMBER_LIFE_MS = 1150
    private static readonly EMBER_LIFE_JITTER = 0.45
    // Fraction of its life a spark fades in over, and the fraction after which it starts going out.
    // The long hold is what makes it a cinder rather than a twinkle.
    private static readonly EMBER_FADE_IN = 0.12
    private static readonly EMBER_FADE_AFTER = 0.3

    // A bubble's life: swells, holds bright for an instant, bursts. One curve, three phases.
    private static readonly BUBBLE_LIFE_MS = 620
    private static readonly BUBBLE_LIFE_JITTER = 0.3
    // How wide a bubble gets, and how bright. Public because they are the visual contract a test can
    // hold the swell-hold-burst curve against: nothing may be stamped onto the floor at full size and
    // full brightness the instant it is born.
    static readonly BUBBLE_SCALE_MIN = 1.1
    static readonly BUBBLE_SCALE_MAX = 2.1
    static readonly BUBBLE_ALPHA_PEAK = 0.62
    // When in its life it pops: it grows up to here, flashes, and is gone over what is left.
    private static readonly BUBBLE_POP_AT = 0.72

    // The box scanned for lava, as a half-extent in rough CELLS around the player: the visible world
    // plus a margin, so a spark can be born just off screen and drift in.
    private static readonly HALF_CELLS_X = ScalingParams.viewportX + 1
    private static readonly HALF_CELLS_Y = ScalingParams.viewportY + 1
    // How often that box is re-walked. Four times a second: fast enough that a flood sparks almost as
    // soon as it is painted, slow enough to be unmeasurable.
    private static readonly REFRESH_MS = 250
    // A long frame (a stall, a backgrounded tab coming back) must not burst-emit the whole backlog.
    private static readonly MAX_FRAME_SECONDS = 0.05
    // Public because it IS the contract: the most sparks one frame may ever produce, however long that
    // frame was and however much lava is on screen.
    static readonly MAX_SPAWN_CATCHUP = 4

    // The two pools. A mote is alive exactly when its sprite is visible, so dying is one boolean and
    // being reborn is `take` finding it again -- no splice, no filter, no allocation on the hot path.
    private embers: Mote[] = []
    private bubbles: Mote[] = []
    private emberDebt = 0
    private bubbleDebt = 0

    // The visible lava cells, as two parallel arrays plus a count. Reused across scans; never
    // reallocated, never sliced.
    private cellX: number[] = []
    private cellY: number[] = []
    private cellCount = 0
    private lastScanMs = 0
    private scannedRoomId: string | undefined

    private texture: Texture | undefined
    private running = false
    private lastMs = 0

    constructor(private makeTexture: () => Texture) {
        const air = new Container()
        air.zIndex = LavaEmbers.Z_INDEX_AIR
        // Everything in the world surface gets hit-tested (Mouse.setWorldRoots), and a spark must never
        // swallow a click meant for the ground under it.
        air.interactiveChildren = false
        air.visible = false
        this.airContainer = air

        const ground = new Container()
        ground.zIndex = LavaEmbers.Z_INDEX_GROUND
        ground.interactiveChildren = false
        ground.visible = false
        this.groundContainer = ground
    }

    // Exposed for tests only -- these allocate, which is why nothing in the loop calls them.
    getEmberCount = () => (this.running ? this.embers.filter(m => m.sprite.visible).length : 0)
    getBubbleCount = () => (this.running ? this.bubbles.filter(m => m.sprite.visible).length : 0)
    getVisibleCellCount = () => (this.running ? this.cellCount : 0)

    update = (
        config: LavaEmbersConfig | null,
        center: Coordinate | undefined,
        provider: TileMapMetaProvider | undefined,
    ) => {
        const cfg = this.resolve(config)
        if (!cfg || !center || !provider) {
            this.stop()
            return
        }

        const nowMs = performance.now()
        if (!this.running) {
            this.running = true
            this.airContainer.visible = true
            this.groundContainer.visible = true
            this.lastMs = nowMs
            // Force the first scan: a room entered with lava already on the floor should spark on the
            // frame it appears, not a quarter second later.
            this.lastScanMs = 0
            this.scannedRoomId = undefined
        }

        this.scanCells(provider, center, nowMs)

        let dt = (nowMs - this.lastMs) / 1000
        this.lastMs = nowMs
        if (!(dt > 0)) {
            return
        }
        dt = Math.min(dt, LavaEmbers.MAX_FRAME_SECONDS)

        this.spawn(dt, cfg)
        this.stepEmbers(dt, cfg)
        this.stepBubbles(dt)
    }

    private stop = () => {
        if (!this.running) {
            return
        }
        this.running = false
        this.airContainer.visible = false
        this.groundContainer.visible = false
        this.embers.forEach(m => (m.sprite.visible = false))
        this.bubbles.forEach(m => (m.sprite.visible = false))
        this.cellCount = 0
        this.emberDebt = 0
        this.bubbleDebt = 0
        this.lastMs = 0
        this.scannedRoomId = undefined
    }

    private resolve = (config: LavaEmbersConfig | null) => {
        const w = window as any
        const forced = typeof w.lavaEmbers === "boolean" ? (w.lavaEmbers as boolean) : undefined
        const enabled = forced !== undefined ? forced : !!config?.enabled
        if (!enabled) {
            return null
        }

        const num = (override: any, value: number | undefined, fallback: number) =>
            typeof override === "number" ? override : typeof value === "number" ? value : fallback

        const scaleMin = config?.scaleMin ?? LavaEmbers.DEFAULT_SCALE_MIN
        const scaleMax = config?.scaleMax ?? LavaEmbers.DEFAULT_SCALE_MAX

        return {
            emberRate: Math.max(0, num(w.lavaEmberRate, config?.emberRate, LavaEmbers.DEFAULT_EMBER_RATE)),
            bubbleRate: Math.max(0, num(w.lavaBubbleRate, config?.bubbleRate, LavaEmbers.DEFAULT_BUBBLE_RATE)),
            alpha: Math.min(1, Math.max(0, num(w.lavaEmberAlpha, config?.alpha, LavaEmbers.DEFAULT_ALPHA))),
            scaleMin: Math.max(0.05, Math.min(scaleMin, scaleMax)),
            scaleMax: Math.max(scaleMin, scaleMax),
            emberColors: config?.emberColors?.length ? config.emberColors : LavaEmbers.DEFAULT_EMBER_COLORS,
            bubbleColors: config?.bubbleColors?.length ? config.bubbleColors : LavaEmbers.DEFAULT_BUBBLE_COLORS,
        }
    }

    /**
     * Re-walk the on-screen box for lava, at most every REFRESH_MS. See the header for why this is a
     * scan rather than a cache: ~200 `isLavaAt` lookups four times a second needs no invalidation hook,
     * picks a runtime flood up by itself, and follows the camera.
     *
     * A room change forces one immediately, whatever the clock says -- the provider is rebuilt per room
     * and last room's cells are not merely stale, they are somewhere else entirely.
     */
    private scanCells = (provider: TileMapMetaProvider, center: Coordinate, nowMs: number) => {
        const roomChanged = this.scannedRoomId !== provider.roomId
        if (!roomChanged && nowMs - this.lastScanMs < LavaEmbers.REFRESH_MS) {
            return
        }
        this.lastScanMs = nowMs
        this.scannedRoomId = provider.roomId

        const cx = Math.floor(center.x / TileSize)
        const cy = Math.floor(center.y / TileSize)
        const minX = Math.max(0, cx - LavaEmbers.HALF_CELLS_X)
        const maxX = Math.min(provider.width() - 1, cx + LavaEmbers.HALF_CELLS_X)
        const minY = Math.max(0, cy - LavaEmbers.HALF_CELLS_Y)
        const maxY = Math.min(provider.height() - 1, cy + LavaEmbers.HALF_CELLS_Y)

        let count = 0
        for (let x = minX; x <= maxX; x++) {
            for (let y = minY; y <= maxY; y++) {
                if (provider.isLavaAt(x, y)) {
                    this.cellX[count] = x
                    this.cellY[count] = y
                    count++
                }
            }
        }
        this.cellCount = count
    }

    private spawn = (dt: number, cfg: NonNullable<ReturnType<LavaEmbers["resolve"]>>) => {
        if (this.cellCount === 0) {
            // No lava on screen: let the debt go rather than banking it, or walking into a cave would
            // fire a saved-up volley on the first frame the pool came into view.
            this.emberDebt = 0
            this.bubbleDebt = 0
            return
        }

        // Per CELL for sparks (a wide pool is livelier), per POOL for bubbles (a bubble is an event you
        // watch, not a density) -- and both capped, which is what pins the cost.
        const emberRate = Math.min(LavaEmbers.MAX_EMBER_RATE, cfg.emberRate * this.cellCount)
        const bubbleRate = Math.min(LavaEmbers.MAX_BUBBLE_RATE, cfg.bubbleRate)

        this.emberDebt = Math.min(this.emberDebt + emberRate * dt, LavaEmbers.MAX_SPAWN_CATCHUP)
        while (this.emberDebt >= 1) {
            this.emberDebt -= 1
            this.emitEmber(cfg)
        }

        this.bubbleDebt = Math.min(this.bubbleDebt + bubbleRate * dt, LavaEmbers.MAX_SPAWN_CATCHUP)
        while (this.bubbleDebt >= 1) {
            this.bubbleDebt -= 1
            this.emitBubble(cfg)
        }
    }

    // Somewhere inside a random visible lava cell. Cells are stamped at (x*TileSize, y*TileSize) by the
    // tilemap renderer, so the cell's interior is that square.
    private pickPoint = (): { x: number; y: number } => {
        const i = Math.floor(Math.random() * this.cellCount)
        return {
            x: (this.cellX[i] + Math.random()) * TileSize,
            y: (this.cellY[i] + Math.random()) * TileSize,
        }
    }

    private emitEmber = (cfg: NonNullable<ReturnType<LavaEmbers["resolve"]>>) => {
        const mote = this.take(this.embers, this.airContainer, LavaEmbers.MAX_EMBERS)
        if (!mote) {
            return
        }
        const at = this.pickPoint()
        mote.x = at.x
        mote.groundY = at.y
        mote.lift = 0
        mote.vx = between(-LavaEmbers.DRIFT, LavaEmbers.DRIFT)
        mote.liftV = LavaEmbers.RISE * between(LavaEmbers.RISE_MIN, LavaEmbers.RISE_MAX)
        mote.ageMs = 0
        mote.lifeMs = LavaEmbers.EMBER_LIFE_MS * between(1 - LavaEmbers.EMBER_LIFE_JITTER, 1 + LavaEmbers.EMBER_LIFE_JITTER)
        mote.scale = between(cfg.scaleMin, cfg.scaleMax)
        mote.peakAlpha = cfg.alpha * between(0.75, 1)
        mote.sprite.visible = true
        mote.sprite.tint = cfg.emberColors[0]
        mote.sprite.alpha = 0
        mote.sprite.scale.set(mote.scale)
        mote.sprite.x = mote.x
        mote.sprite.y = mote.groundY
    }

    private emitBubble = (cfg: NonNullable<ReturnType<LavaEmbers["resolve"]>>) => {
        const mote = this.take(this.bubbles, this.groundContainer, LavaEmbers.MAX_BUBBLES)
        if (!mote) {
            return
        }
        const at = this.pickPoint()
        mote.x = at.x
        mote.groundY = at.y
        mote.lift = 0
        mote.vx = 0
        mote.liftV = 0
        mote.ageMs = 0
        mote.lifeMs =
            LavaEmbers.BUBBLE_LIFE_MS * between(1 - LavaEmbers.BUBBLE_LIFE_JITTER, 1 + LavaEmbers.BUBBLE_LIFE_JITTER)
        mote.scale = between(LavaEmbers.BUBBLE_SCALE_MIN, LavaEmbers.BUBBLE_SCALE_MAX)
        mote.peakAlpha = LavaEmbers.BUBBLE_ALPHA_PEAK * cfg.alpha
        mote.sprite.visible = true
        // A bubble holds ONE shade for its whole life -- it is the body of the lava turning over, not
        // something cooling in the air, so there is no ramp to walk and the tint is set once here.
        mote.sprite.tint = cfg.bubbleColors[Math.floor(Math.random() * cfg.bubbleColors.length)]
        mote.sprite.alpha = 0
        mote.sprite.scale.set(0)
        mote.sprite.x = mote.x
        mote.sprite.y = mote.groundY
    }

    // The next dead mote in a pool, growing the pool up to its cap. Returns undefined at the cap: the
    // newest spark is the one to drop, because dropping it is invisible and a frame spent on four
    // hundred sprites is not (FootDust's MAX_PUFFS makes the same call).
    private take = (pool: Mote[], parent: Container, cap: number): Mote | undefined => {
        for (let i = 0; i < pool.length; i++) {
            if (!pool[i].sprite.visible) {
                return pool[i]
            }
        }
        if (pool.length >= cap) {
            return undefined
        }
        if (!this.texture) {
            try {
                this.texture = this.makeTexture()
            } catch (e) {
                this.texture = Texture.WHITE
            }
        }
        const sprite = new Sprite(this.texture)
        sprite.anchor.set(0.5)
        // Molten rock is light, not a pale sprite -- the same choice the campfire and the fireflies make.
        sprite.blendMode = BLEND_MODES.ADD
        sprite.visible = false
        parent.addChild(sprite)
        const mote: Mote = {
            sprite,
            x: 0,
            groundY: 0,
            lift: 0,
            vx: 0,
            liftV: 0,
            ageMs: 0,
            lifeMs: 1,
            scale: 1,
            peakAlpha: 1,
        }
        pool.push(mote)
        return mote
    }

    private stepEmbers = (dt: number, cfg: NonNullable<ReturnType<LavaEmbers["resolve"]>>) => {
        const dtMs = dt * 1000
        for (let i = 0; i < this.embers.length; i++) {
            const m = this.embers[i]
            if (!m.sprite.visible) {
                continue
            }
            m.ageMs += dtMs
            const t = m.ageMs / m.lifeMs
            if (t >= 1) {
                m.sprite.visible = false
                continue
            }

            // Rises and slows: buoyancy bleeding away, so it settles into a drift rather than climbing
            // out of the room.
            m.liftV -= m.liftV * LavaEmbers.RISE_DRAG * dt
            m.lift += m.liftV * dt
            m.x += m.vx * dt

            // Fade in, hold, fade out. The hold is what makes it a cinder you can look at.
            const glow =
                t < LavaEmbers.EMBER_FADE_IN
                    ? t / LavaEmbers.EMBER_FADE_IN
                    : t > LavaEmbers.EMBER_FADE_AFTER
                    ? 1 - (t - LavaEmbers.EMBER_FADE_AFTER) / (1 - LavaEmbers.EMBER_FADE_AFTER)
                    : 1

            const sprite = m.sprite
            sprite.x = m.x
            // Drawn at the lift, sorted nowhere in particular -- the whole container has one z (see
            // the header), so unlike FootDust there is no ground line to keep separate. The lift is
            // still tracked apart from the spawn point so a spark cannot drift out of its own cell.
            sprite.y = m.groundY - m.lift
            sprite.alpha = m.peakAlpha * glow
            // Cools as it goes: shrinking AND reddening, which together read as heat leaving it.
            sprite.scale.set(m.scale * (1 - 0.45 * t))
            sprite.tint = rampAt(cfg.emberColors, t)
        }
    }

    private stepBubbles = (dt: number) => {
        const dtMs = dt * 1000
        for (let i = 0; i < this.bubbles.length; i++) {
            const m = this.bubbles[i]
            if (!m.sprite.visible) {
                continue
            }
            m.ageMs += dtMs
            const t = m.ageMs / m.lifeMs
            if (t >= 1) {
                m.sprite.visible = false
                continue
            }

            const sprite = m.sprite
            if (t < LavaEmbers.BUBBLE_POP_AT) {
                // Swelling. Eased, so it domes up instead of inflating linearly.
                const u = t / LavaEmbers.BUBBLE_POP_AT
                const eased = u * u * (3 - 2 * u)
                sprite.scale.set(m.scale * (0.25 + 0.75 * eased))
                sprite.alpha = m.peakAlpha * eased
            } else {
                // The burst: one quick flare wider than the bubble ever got, going out as it spreads.
                const u = (t - LavaEmbers.BUBBLE_POP_AT) / (1 - LavaEmbers.BUBBLE_POP_AT)
                sprite.scale.set(m.scale * (1 + 0.55 * u))
                sprite.alpha = m.peakAlpha * (1 - u) * (1 - u)
            }
        }
    }

    /**
     * The spark, generated once: a tight bright core inside a soft halo, drawn white so one texture
     * serves every tint and both pools -- one BaseTexture means each container is one draw call.
     *
     * Stacked translucent circles rather than a blur filter, for the reason in the header. Tighter and
     * smaller than the firefly glow it is modelled on: a firefly is a lamp coming on and a spark is a
     * speck of burning rock, so the core has to survive being drawn at a third of this size.
     */
    static buildEmberTexture = (generate: (g: Graphics) => Texture): Texture => {
        const g = new Graphics()
        const RINGS = 6
        const OUTER = 7
        for (let i = RINGS; i >= 1; i--) {
            const radius = (OUTER * i) / RINGS
            // Falls off steeply, so the halo is a suggestion and the core is the thing.
            const alpha = 0.11 * Math.pow(1 - (i - 1) / RINGS, 1.9)
            g.beginFill(0xffffff, alpha)
            g.drawCircle(0, 0, radius)
            g.endFill()
        }
        g.beginFill(0xffffff, 0.98)
        g.drawCircle(0, 0, 1.8)
        g.endFill()
        const texture = generate(g)
        g.destroy()
        return texture
    }

    static textureFactory = (renderer: { generateTexture: (g: Graphics, options?: any) => Texture }) => (): Texture =>
        LavaEmbers.buildEmberTexture(g =>
            renderer.generateTexture(g, { resolution: 2, scaleMode: SCALE_MODES.LINEAR }),
        )
}

const between = (min: number, max: number): number => min + Math.random() * (max - min)

/**
 * Interpolate a 0xRRGGBB ramp at `t` in 0..1. Per channel, which is good enough for three stops of
 * orange and costs a handful of flops -- a proper perceptual blend would be the right answer for a
 * gradient the eye can study and is the wrong answer for a 3px dot that lives a second.
 */
export const rampAt = (colors: number[], t: number): number => {
    if (colors.length === 0) {
        return 0xffffff
    }
    if (colors.length === 1) {
        return colors[0]
    }
    const u = Math.min(1, Math.max(0, t)) * (colors.length - 1)
    const i = Math.min(colors.length - 2, Math.floor(u))
    const f = u - i
    const a = colors[i]
    const b = colors[i + 1]
    const r = ((a >> 16) & 0xff) + (((b >> 16) & 0xff) - ((a >> 16) & 0xff)) * f
    const gc = ((a >> 8) & 0xff) + (((b >> 8) & 0xff) - ((a >> 8) & 0xff)) * f
    const bc = (a & 0xff) + ((b & 0xff) - (a & 0xff)) * f
    return (Math.round(r) << 16) | (Math.round(gc) << 8) | Math.round(bc)
}
